/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect } from "react"
import { Button, Modal } from "@duxton-web/ui"

import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { RETURN_REFUND_CHAT_SOURCE_STATE } from "ecommerce/constants/returnRefund"
import { EventAction, EventCategory } from "ecommerce/constants/tracking"
import { TroyWebTracker } from "utils/tracking"

import styles from "./ChatUnavailableModal.modules.css"

interface ChatUnavailableModalProps {
  open: boolean
  /** Interpolated into the body copy so the merchant can find the request in the app. */
  orderID: string
  /** The drawer's current header status, e.g. `ACKNOWLEDGING_REQUEST`. */
  returnRefundStatus?: string
  onClose(): void
}

/**
 * Terminal modal for the drawer's chat icon. Buyer chat only exists in the app, so on web
 * there is no availability check and no chat UI — just directions and a dismiss.
 */
export const ChatUnavailableModal: FC<ChatUnavailableModalProps> = ({
  open,
  orderID,
  returnRefundStatus,
  onClose,
}) => {
  const text = useTranslationsMap({
    chatUnavailableCta: "ecommerce.sellerCentre.returns.chatUnavailableCta",
    chatUnavailableTitle: "ecommerce.sellerCentre.returns.chatUnavailableTitle",
  })
  // `orderID` is a prop, known up front — `useTranslation` substitutes it directly.
  const chatUnavailableBody = useTranslation(
    "ecommerce.sellerCentre.returns.chatUnavailableBody",
    { "<name>": orderID },
  )

  useEffect(() => {
    if (!open) {
      return
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.returnRefundChatUnavailablePopup,
      action: EventAction.pageView,
      labels: {
        ORDER_ID: orderID,
        RETURN_REFUND_STATUS: returnRefundStatus ?? null,
        SOURCE_STATE: RETURN_REFUND_CHAT_SOURCE_STATE.details,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleGotIt = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.returnRefundChatUnavailablePopup,
      action: EventAction.clickGotIt,
      labels: {
        ORDER_ID: orderID,
      },
    })
    onClose()
  }

  const handleCancel = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.returnRefundChatUnavailablePopup,
      action: EventAction.clickClose,
      labels: {
        ORDER_ID: orderID,
      },
    })
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      /*
       * `onCancel` is Duxton's single hook for the close icon, the mask click and Escape
       * alike — disabling the other two dismiss paths is what makes `CLICK_CLOSE`
       * actually mean "the merchant tapped X", matching the spec's trigger.
       */
      maskClosable={false}
      keyboard={false}
      title={text.chatUnavailableTitle}
      className={styles.modal}
      footer={
        <Button type="primary" className={styles.cta} onClick={handleGotIt}>
          {text.chatUnavailableCta}
        </Button>
      }
    >
      <p className={styles.body}>{chatUnavailableBody}</p>
    </Modal>
  )
}
